
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";


import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "@mui/material";
import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";


import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";


import SideNav from "component/layouts/sideNav";
import { useDebounce } from "resources/constant/utilities/util";

import {
  ADD,
  CREDIT_NOTE,
  DELETE,
  GO,
  MATERIAL_MANAGEMENT,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_CREDIT_NOTE,
  PATH_CREDIT_NOTE_DETAIL,
  PATH_LOGIN,
} from "resources/constant/url/url";
import {
  setFilterModel,
  setSortModel,
  setDates,
} from "store/Slice/creditNoteUiSlice";


import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APIMATERIALMGMT } from "apihandler/materialmgmt";


import {
  setItemCategories,
  setItemList,
  setItems,
  setSiteCategories,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setTaxes,
  setUoms,
  siteCellRenderer,
  supplierCellRenderer,
  setUsers,
  userCellRenderer,
} from "component/cellRenderers/renderers";


import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";


export default function CreditNote() {
  const { date } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const creditNoteCacheRef = useRef({});
  const uiState = useSelector((s) => s.creditNoteUi);

  const getCacheKey = (from, to) =>
  `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;

  const gridRef = useRef();

  const [dateValue, setDateValue] = useState(
    uiState?.dateFromISO ? dayjs(uiState.dateFromISO) : null
  );
  const [toDateValue, setToDateValue] = useState(
    uiState?.dateToISO ? dayjs(uiState.dateToISO) : dayjs().endOf("month")
  );

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  useEffect(() => {
    dispatch(
      setDates({
        fromISO: dateValue ? dayjs(dateValue).format("YYYY-MM-DD") : null,
      })
    );
  }, [dateValue, dispatch]);
  useEffect(() => {
    dispatch(
      setDates({
        toISO: toDateValue ? dayjs(toDateValue).format("YYYY-MM-DD") : null,
      })
    );
  }, [toDateValue, dispatch]);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const deleteModel = useCallback(() => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent?.isConfirmed) return;

      APIMATERIALMGMT.DeleteCreditNotes(selectedRowIds)
        .then((resp) => resp.json().catch(() => ({})))
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  }, [selectedRowIds]);

  const onRowSelectionChanged = useCallback((event) => {
    const ids = event.api.getSelectedRows().map((r) => r.id);
    setSelectedRowIds(ids);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        flex: 1.5,
        headerName: "C-GRN No",
        field: "creditNoteGRNNumber",
        checkboxSelection: false,
        cellStyle: { "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        flex: 1.1,
        headerName: "Credit Note Date",
        field: "date",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Credit Note No",
        field: "creditNoteNumber",
        filter: false,
        floatingFilter: false,
        cellStyle: { "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Supplier Name",
        field: "supplierId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (params) =>
          supplierCellRenderer({ value: params.data.supplierId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Site",
        field: "siteId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (params) =>
          siteCellRenderer({ value: params.data.siteId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Bill No",
        field: "billNo",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Bill Date",
        field: "billDate",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Basic Cost",
        field: "basicCost",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Tax Cost",
        field: "taxCost",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Grand Total",
        field: "grandTotal",
        filter: false,
        floatingFilter: false,
        cellStyle: { textAlign: "center", "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Modified By",
        field: "modifiedBy",
        valueGetter: (params) =>
          userCellRenderer({ value: params.data.modifiedBy }),
        cellStyle: { "border-right-color": "#e2e2e2" },
        autoHeight: true,
        wrapText: true,
      },
      {
        flex: 0.8,
        headerName: "Details",
        field: "id",
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              aria-label="edit"
              onClick={() => {
                const safeDate = (dateValue || dayjs()).format("YYYY-MM-DD");
                navigate(PATH_CREDIT_NOTE_DETAIL + params.data.id, {
                  state: { date: safeDate, isEdit: false },
                });
              }}
            >
              <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          </div>
        ),
      },
    ],
    [dateValue, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      editable: false,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: { caseSensitive: false, defaultOption: "startsWith" },
      floatingFilter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    []
  );

  const fetchCreditNotes = useCallback((d1, d2) => {
    const from = (d1 || dayjs().startOf("month")).format("YYYY-MM-DD");
    const to = (d2 || dayjs().endOf("month")).format("YYYY-MM-DD");
    return APIMATERIALMGMT.CreditNotes(from, to)
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return (resp.response || []).sort((a, b) => b.id - a.id);
      });
  }, []);

  const loadCreditNotesWithCache = useCallback(
    async (fromDate, toDate) => {
      try {
        setIsLoading(true);
        if (!gridRef.current?.api) return;

        const from = fromDate || dayjs().startOf("month");
        const to = toDate || dayjs().endOf("month");

        const cacheKey = getCacheKey(from, to);
        let data;

        if (creditNoteCacheRef.current[cacheKey]) {
          data = creditNoteCacheRef.current[cacheKey];
        } else {
          data = await fetchCreditNotes(from, to);
          creditNoteCacheRef.current[cacheKey] = data;
        }

        try {
          gridRef.current.api.setRowData(data);
        } catch (e) {
          try {
            const rowData = [];
            gridRef.current.api.forEachNode((n) => rowData.push(n.data));
            if (rowData.length) {
              gridRef.current.api.applyTransaction({ remove: rowData });
            }
            gridRef.current.api.applyTransaction({ add: data });
          } catch (_) {}
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCreditNotes]
  );
  const debouncedLoadCreditNotes = useDebounce((from, to) => loadCreditNotesWithCache(from, to),300);

  const rehydrateGridUi = useCallback(
    (api) => {
      if (!api || !uiState) return;
      if (uiState.filterModel) api.setFilterModel(uiState.filterModel);
      if (uiState.sortModel) api.setSortModel(uiState.sortModel);
    },
    [uiState]
  );

  const onGridReady = useCallback(
    (params) => {
      if (!uiState?.dateFromISO) {
        if (date) setDateValue(dayjs(date));
        else setDateValue(dayjs().startOf("month"));
      }

      APIMASTER.Uoms()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode !== 200) return;
          resp.response.forEach((x) => {
            x.value = x.id;
            x.name = x.uomName;
          });
          setUoms(resp.response);
        });
      APIMASTER.TaxOptions()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setTaxes(resp.response);
        });
      APIMASTER.SupplierOptions()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setSuppliers(resp.response);
        });
      APIMASTER.SupplierCategories()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setSupplierCategories(resp.response);
        });
      APIMASTER.SiteCategories()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setSiteCategories(resp.response);
        });
      APIMASTER.SiteOptions()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setSites(resp.response);
        });
      APIMASTER.ItemCategories()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setItemCategories(resp.response);
        });
      APIMASTER.Items()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode !== 200) return;
          const itemOptions = (resp.response || []).map((element) => ({
            name: element.itemName,
            value: element.id,
            dependentvalue: element.categoryId,
          }));
          setItems(itemOptions);
          setItemList(resp.response || []);
        });

      const from = uiState?.dateFromISO
        ? dayjs(uiState.dateFromISO)
        : date
          ? dayjs(date)
          : dayjs().startOf("month");
      const to = uiState?.dateToISO
        ? dayjs(uiState.dateToISO)
        : dayjs().endOf("month");

      fetchCreditNotes(from, to).then((data) => {
        params.api.setRowData(data);
        rehydrateGridUi(params.api);
      });

      APIMASTER.UserOptions()
        .then((r) => r.json())
        .then((resp) => {
          if (resp.statusCode === 200) setUsers(resp.response);
        });
    },
    [date, fetchCreditNotes, rehydrateGridUi, uiState]
  );

  const onFilterChanged = useCallback(
    (e) => {
      const model = e.api.getFilterModel();
      dispatch(setFilterModel(model));
    },
    [dispatch]
  );

  const onSortChanged = useCallback(
    (e) => {
      const model = e.api.getSortModel();
      dispatch(setSortModel(model));
    },
    [dispatch]
  );

  return (
    <SideNav
      currentComponent={MATERIAL_MANAGEMENT}
      subComponent={CREDIT_NOTE}
      subComponentPath={PATH_CREDIT_NOTE}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            const safeDate = (dateValue || dayjs()).format("YYYY-MM-DD");
            navigate(PATH_CREDIT_NOTE_DETAIL + 0, {
              state: { date: safeDate, isEdit: false },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>
      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div align="right" style={{ width: "100%", right: 0 }}>
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              debouncedLoadCreditNotes(dateValue, toDateValue);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            suppressBrowserResizeObserver={true}
          ></AgGridReact>
        </div>
      </div>

    </SideNav>
  );
}
